import Config from "vamos/cockpit/config/Config";
import { ResponseLoginResponse } from "vamos/cockpit/model/api/vamos.public.api.types";
import { Client, ResponseListClient } from "vamos/cockpit/model/api/vamos.private.api.types";
import ApiService from "vamos/cockpit/base/services/type/ApiService";
import AuthService from "vamos/cockpit/base/services/type/AuthService";
import ComponentContainer from "sap/ui/core/ComponentContainer";
import Shell from "sap/m/Shell";
import UserHandler from "vamos/cockpit/data/user/handler/UserHandler";
import AppResponse from "vamos/cockpit/model/api/AppResponse";
import ServerUnreachableScreen from "vamos/cockpit/login/ServerUnreachableScreen";

export default class LoginService {
    private userLoggedIn: boolean = false;

    public checkLogin(): void {
        if (!this.userLoggedIn) {
            ApiService.getInstance().getRestJsonData(
                "/user/login", true).then(this.afterCheckLogin.bind(this),
                (data) => {
                    console.error(data);
                }
            );
        } else {
            void this.loadApp();
        }
    }

    private async afterCheckLogin(result: ResponseLoginResponse) {
            if(result.content.userLoggedIn) {
                await this.loadApp();
            } else {
                if (this.hasCiamCode()) {
                    ApiService.getInstance().postRestJsonDataWithoutMessage("/user/login-with-ciam", { code: this.getCiamCode() }, true)
                        .then(this.afterCiamLogin.bind(this),
                            (data) => {
                                console.error(data);}
                        );
                } else {
                    this.redirectToLogin();
                }
            }
    }

    private async afterCiamLogin(result: AppResponse<ResponseLoginResponse>) {
        if(result.data.content.userLoggedIn) {
            await this.loadApp();
        } else {
            this.redirectToLogin();
        }
    }

    public async loadApp() {

        if (!await this.ensureClientGuid()) {
            this.redirectToLogin();
            return;
        }

        await UserHandler.getInstance().loadUser();
        const oUser = await UserHandler.getInstance().getUser();
        if (oUser.IsOnlyInstaller() && !oUser.hasModule("SHOPPING_CART")) {
            this.logoutUser();
            return;
        }

        const oComponentContainer = new ComponentContainer({
            id: "vamos",
            name: "vamos.cockpit",
            manifest: true,
            lifecycle: "Container",
            handleValidation: true,
            settings: { id: "vamos_component" },
        });
        const shell = new Shell({
            id: "AppShell",
            app: oComponentContainer,
            showLogout: false,
            appWidthLimited: false,
        });
        shell.placeAt("root");
    }

    /**
     * Every authenticated backend call needs an X-Client-Guid header, so the client must be known before the app starts. Normally the login app
     * has already stored it; if it has not (CIAM login, a token taken over from the debug settings), the client list - one of the few resources
     * reachable without a selected client - provides it.
     */
    private async ensureClientGuid(): Promise<boolean> {
        if (AuthService.getClientGuid()) {
            return true;
        }
        try {
            const clientListResponse: AppResponse<ResponseListClient> =
                await ApiService.getInstance().getRestJsonDataWithoutMessage("/user/clients");
            const clientList = clientListResponse.data?.content ?? [];
            const client = clientList.find((entry: Client) => entry.selected) ?? clientList[0];
            if (!client?.guid) {
                return false;
            }
            AuthService.setClientGuid(client.guid);
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    public redirectToLogin() {
        self.location.href = Config.getConfigValue("LoginUrl");
    }

    /**
     * Logging out needs no backend call - the backend holds no session state, so dropping the token locally is the whole operation.
     */
    public logoutUser() {
        AuthService.clear();
        this.userLoggedIn = false;
        this.redirectToLogin();
    }

    private hasCiamCode(): boolean {
        const oParams = new URLSearchParams(window.location.search);
        return oParams.has("code");
    }

    private getCiamCode(): string {
        const oParams = new URLSearchParams(window.location.search);
        return oParams.get("code");
    }

}
